// Comprehensive Mock Firebase SDK — Auth + Firestore
// Routes all requests to local JWT/MongoDB microservice via relative /api/firebase/* paths.
// This file is a drop-in replacement for the Firebase Web SDK (v9 modular API).

const API_ROOT = ''; // Reverse proxy maps /api/firebase/* → firebase-api:5003/

// =============================================================================
// HELPERS
// =============================================================================

function getRequestHeaders() {
  const token = localStorage.getItem('jwt_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/** Build a fully-featured mock user object with all Firebase methods expected by the app. */
function createUserObject(uid, email) {
  if (!uid) return null;
  return {
    uid,
    email,
    displayName: email ? email.split('@')[0] : '',
    emailVerified: true,
    // Firebase User.getIdToken() — returns the stored JWT token as a promise
    getIdToken: (_forceRefresh) => Promise.resolve(localStorage.getItem('jwt_token') || ''),
    // Firebase User.getIdTokenResult() — wraps token in a result-like object
    getIdTokenResult: (_forceRefresh) => Promise.resolve({
      token: localStorage.getItem('jwt_token') || '',
      claims: {},
    }),
    reload: () => Promise.resolve(),
    toJSON: () => ({ uid, email }),
  };
}

/** Extract (collectionPath, docId) from a Firestore-like doc path string. */
function splitDocPath(path) {
  const lastSlash = path.lastIndexOf('/');
  return {
    colPath: path.substring(0, lastSlash),
    docId: path.substring(lastSlash + 1),
  };
}

/** Build a mock QuerySnapshot compatible with both .docs[] and .forEach(). */
function buildQuerySnapshot(rawDocs) {
  const docsArray = rawDocs.map((d) => {
    const id = d.id || d._id || '';
    // Attach a `.ref` doc reference so batch.delete(d.ref) works
    const ref = { type: 'doc', path: `${d._collection || ''}/${id}`, id };
    return {
      id,
      ref,
      exists: () => true,
      data: () => {
        const { _collection, id: _id, _id: __id, ...rest } = d;
        return rest;
      },
    };
  });
  return {
    docs: docsArray,
    forEach: (cb) => docsArray.forEach(cb),
    get empty() { return docsArray.length === 0; },
    get size() { return docsArray.length; },
  };
}

// =============================================================================
// FIREBASE APP (stub — app init is no-op; all state lives in auth singleton)
// =============================================================================
export const initializeApp = (_config) => ({ name: '[MockFirebaseApp]' });

// =============================================================================
// AUTH MOCK
// =============================================================================

class MockAuth {
  constructor() {
    this._listeners = [];
    const cached = localStorage.getItem('user');
    const token = localStorage.getItem('jwt_token');
    if (cached && token) {
      try {
        const parsed = JSON.parse(cached);
        this.currentUser = createUserObject(parsed.uid, parsed.email);
      } catch {
        this.currentUser = null;
        localStorage.removeItem('user');
        localStorage.removeItem('jwt_token');
      }
    } else {
      if (cached && !token) {
        localStorage.removeItem('user');
      }
      this.currentUser = null;
    }
  }

  /** Matches Firebase's modular onAuthStateChanged(auth, callback) pattern. */
  onAuthStateChanged(callback) {
    this._listeners.push(callback);
    // Fire immediately with current state (Firebase SDK behaviour)
    try { callback(this.currentUser); } catch (e) { console.error('[Auth] listener error:', e); }
    return () => {
      this._listeners = this._listeners.filter((l) => l !== callback);
    };
  }

  _notifyListeners() {
    this._listeners.forEach((cb) => {
      try { cb(this.currentUser); } catch (e) { console.error('[Auth] listener error:', e); }
    });
  }
}

export const auth = new MockAuth();

export function getAuth(_app) { return auth; }

// Standalone modular function: onAuthStateChanged(auth, callback)
export function onAuthStateChanged(authInstance, callback) {
  return authInstance.onAuthStateChanged(callback);
}

// AUTH API CALLS -------------------------------------------------------------------

export async function signInWithEmailAndPassword(_authInstance, email, password) {
  const res = await fetch(`${API_ROOT}/api/firebase/auth/signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Authentication failed');
    err.code = data.code || 'auth/invalid-credential';
    throw err;
  }
  localStorage.setItem('jwt_token', data.token);
  localStorage.setItem('user', JSON.stringify({ uid: data.uid, email: data.email }));
  auth.currentUser = createUserObject(data.uid, data.email);
  auth._notifyListeners();
  return { user: auth.currentUser };
}

export async function signInWithCustomToken(_authInstance, token) {
  const res = await fetch(`${API_ROOT}/api/firebase/auth/signin-custom-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'SSO token sign-in failed');
    err.code = 'auth/invalid-custom-token';
    throw err;
  }
  localStorage.setItem('jwt_token', data.token);
  localStorage.setItem('user', JSON.stringify({ uid: data.uid, email: data.email }));
  auth.currentUser = createUserObject(data.uid, data.email);
  auth._notifyListeners();
  return { user: auth.currentUser };
}

export async function signOut(_authInstance) {
  localStorage.removeItem('jwt_token');
  localStorage.removeItem('user');
  auth.currentUser = null;
  auth._notifyListeners();
}

// =============================================================================
// FIRESTORE MOCK
// =============================================================================
export const db = { type: '[MockFirestore]' };

export function getFirestore(_app) { return db; }

// REFERENCE BUILDERS ------------------------------------------------------------------

/** collection(db, 'myCollection') or collection(docRef, 'subCollection') */
export function collection(parentOrDb, ...pathParts) {
  let basePath = '';
  if (parentOrDb && parentOrDb.type === 'doc') {
    basePath = parentOrDb.path;
  } else if (parentOrDb && parentOrDb.type === 'collection') {
    basePath = parentOrDb.path;
  }
  const subPath = pathParts.join('/');
  const fullPath = basePath ? `${basePath}/${subPath}` : subPath;
  return { type: 'collection', path: fullPath };
}

/** doc(db, 'col', 'docId') or doc(colRef, 'docId') or doc(docRef, 'sub', 'id') */
export function doc(parent, ...pathParts) {
  let fullPath = '';
  if (parent && (parent.type === 'collection' || parent.type === 'doc')) {
    fullPath = `${parent.path}/${pathParts.join('/')}`;
  } else {
    // doc(db, 'col/docId') style
    fullPath = pathParts.join('/');
  }
  const id = fullPath.split('/').pop();
  return { type: 'doc', path: fullPath, id };
}

export function query(collectionRef, ...constraints) {
  return { type: 'query', collection: collectionRef, constraints };
}

// QUERY CONSTRAINTS -------------------------------------------------------------------

export function where(field, op, value) {
  return { type: 'where', field, op, value };
}

export function orderBy(field, direction = 'asc') {
  return { type: 'orderBy', field, direction };
}

export function limit(value) {
  return { type: 'limit', value };
}

export function startAfter(...values) {
  return { type: 'startAfter', values };
}

// DOCUMENT OPERATIONS -----------------------------------------------------------------

export async function getDoc(docRef) {
  const { colPath, docId } = splitDocPath(docRef.path);
  const res = await fetch(
    `${API_ROOT}/api/firebase/generic/collections/${encodeURIComponent(colPath)}/documents/${encodeURIComponent(docId)}`,
    { method: 'GET', headers: getRequestHeaders() }
  );
  if (!res.ok) throw new Error(`getDoc failed: ${res.statusText}`);
  const data = await res.json();
  return {
    id: docId,
    ref: docRef,
    exists: () => !!data.exists,
    data: () => (data.exists ? data.data : undefined),
  };
}

export async function getDocs(queryRef) {
  let collectionPath = '';
  let constraints = [];

  if (queryRef.type === 'query') {
    collectionPath = queryRef.collection.path;
    constraints = queryRef.constraints || [];
  } else {
    collectionPath = queryRef.path;
  }

  const bodyPayload = { filters: [], orderBy: [], limit: 0 };
  for (const c of constraints) {
    if (c.type === 'orderBy') bodyPayload.orderBy.push({ field: c.field, direction: c.direction });
    else if (c.type === 'limit') bodyPayload.limit = c.value;
    else if (c.type === 'where') bodyPayload.filters.push({ field: c.field, op: c.op, value: c.value });
  }

  const res = await fetch(
    `${API_ROOT}/api/firebase/generic/collections/${encodeURIComponent(collectionPath)}/query`,
    { method: 'POST', headers: getRequestHeaders(), body: JSON.stringify(bodyPayload) }
  );
  if (!res.ok) throw new Error(`getDocs failed: ${res.statusText}`);
  const data = await res.json();
  return buildQuerySnapshot(data.docs || []);
}

export async function setDoc(docRef, data, options) {
  const { colPath, docId } = splitDocPath(docRef.path);
  const res = await fetch(
    `${API_ROOT}/api/firebase/generic/collections/${encodeURIComponent(colPath)}/documents/${encodeURIComponent(docId)}`,
    {
      method: 'POST',
      headers: getRequestHeaders(),
      body: JSON.stringify({ data, merge: options?.merge !== false }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'setDoc failed');
  }
}

export async function updateDoc(docRef, data) {
  const { colPath, docId } = splitDocPath(docRef.path);
  const res = await fetch(
    `${API_ROOT}/api/firebase/generic/collections/${encodeURIComponent(colPath)}/documents/${encodeURIComponent(docId)}`,
    { method: 'PATCH', headers: getRequestHeaders(), body: JSON.stringify({ data }) }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'updateDoc failed');
  }
}

export async function deleteDoc(docRef) {
  const { colPath, docId } = splitDocPath(docRef.path);
  const res = await fetch(
    `${API_ROOT}/api/firebase/generic/collections/${encodeURIComponent(colPath)}/documents/${encodeURIComponent(docId)}`,
    { method: 'DELETE', headers: getRequestHeaders() }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'deleteDoc failed');
  }
}

// NETWORK CONTROL (no-ops — MongoDB is always reachable via API) ----------------------

export const enableNetwork = (_db) => Promise.resolve();
export const disableNetwork = (_db) => Promise.resolve();

// FIELD VALUE OPERATORS ---------------------------------------------------------------

export function arrayUnion(...elements) {
  return { _method: 'arrayUnion', value: elements.length === 1 ? elements[0] : elements };
}

export function arrayRemove(...elements) {
  return { _method: 'arrayRemove', value: elements.length === 1 ? elements[0] : elements };
}

export function increment(value) {
  return { _method: 'increment', value };
}

export function deleteField() {
  return { _method: 'deleteField' };
}

// TIMESTAMP MOCK -----------------------------------------------------------------------

export const serverTimestamp = () => new Date().toISOString();

/**
 * Mock Timestamp class — must be a real class (not a plain object)
 * so that `x instanceof Timestamp` works in firebaseService.js.
 */
export class Timestamp {
  constructor(seconds, nanoseconds = 0) {
    this.seconds = seconds;
    this.nanoseconds = nanoseconds;
  }

  toDate() {
    return new Date(this.seconds * 1000);
  }

  toMillis() {
    return this.seconds * 1000;
  }

  static now() {
    return new Timestamp(Math.floor(Date.now() / 1000), 0);
  }

  static fromDate(date) {
    return new Timestamp(Math.floor(date.getTime() / 1000), 0);
  }

  static fromMillis(ms) {
    return new Timestamp(Math.floor(ms / 1000), 0);
  }
}

// WRITE BATCH -------------------------------------------------------------------------

class MockWriteBatch {
  constructor() {
    this._operations = [];
  }

  set(docRef, data, options) {
    const { colPath, docId } = splitDocPath(docRef.path);
    this._operations.push({ type: 'set', path: colPath, doc_id: docId, data, merge: options?.merge !== false });
    return this;
  }

  update(docRef, data) {
    const { colPath, docId } = splitDocPath(docRef.path);
    this._operations.push({ type: 'update', path: colPath, doc_id: docId, data });
    return this;
  }

  delete(docRef) {
    // Accept both a plain doc ref and a snapshot's .ref (both have .path)
    const path = docRef.path || (docRef.type === 'doc' ? docRef.path : '');
    const { colPath, docId } = splitDocPath(path);
    this._operations.push({ type: 'delete', path: colPath, doc_id: docId });
    return this;
  }

  async commit() {
    if (this._operations.length === 0) return;
    const res = await fetch(`${API_ROOT}/api/firebase/generic/batch`, {
      method: 'POST',
      headers: getRequestHeaders(),
      body: JSON.stringify({ operations: this._operations }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Batch commit failed');
    }
  }
}

export function writeBatch(_db) {
  return new MockWriteBatch();
}